import { useState, useEffect } from 'react';
import HeroSection from './components/HeroSection';
import IndexSection from './components/IndexSection';
import TopicsSection from './components/TopicsSection';
import ContactSection from './components/ContactSection';
import AboutSection from './components/AboutSection';
import CardsSection from './components/CardsSection';
import RankingSection from './components/RankingSection';
import UpdatesSection from './components/UpdatesSection';
import FAQSection from './components/FAQSection';
import MediaSection from './components/MediaSection';
import FeaturesSection from './components/FeaturesSection';
import CommentsSection from './components/CommentsSection';
import AppSection from './components/AppSection';
import CarouselSection from './components/CarouselSection';

const navItems = [
  { label: '首页', href: '#hero' },
  { label: '热门话题', href: '#topics' },
  { label: '精选推荐', href: '#cards' },
  { label: '热度榜单', href: '#ranking' },
  { label: '最新动态', href: '#updates' },
  { label: '影迷热评', href: '#comments' },
];

export default function Home() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMobileMenuOpen(false);
    const el = document.querySelector(href);
    if (el) {
      const headerHeight = 72;
      const top = el.getBoundingClientRect().top + window.pageYOffset - headerHeight;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div className="relative min-h-screen bg-background-50">
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-[72px]">
            <a
              href="#hero"
              onClick={(e) => { e.preventDefault(); handleNavClick('#hero'); }}
              className="flex items-center gap-2 flex-shrink-0 cursor-pointer"
            >
              <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-movie-line text-lg text-background-50"></i>
              </div>
              <span className={`font-heading text-lg font-bold transition-colors duration-300 ${scrolled ? 'text-foreground-900' : 'text-background-50'}`}>
                被窝影院
              </span>
            </a>

            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => { e.preventDefault(); handleNavClick(item.href); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    scrolled
                      ? 'text-foreground-600 hover:text-primary-600 hover:bg-primary-50'
                      : 'text-background-200/80 hover:text-background-50 hover:bg-background-50/10'
                  }`}
                >
                  {item.label}
                </a>
              ))}
            </nav>

            <div className="hidden md:flex items-center gap-3">
              <a
                href="#cards"
                onClick={(e) => { e.preventDefault(); handleNavClick('#cards'); }}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                    : 'bg-background-50/15 text-background-50 hover:bg-background-50/25 backdrop-blur-sm'
                }`}
              >
                <i className="ri-play-circle-line mr-1.5"></i>开始观影
              </a>
            </div>

            <button
              className="md:hidden w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="菜单"
            >
              <i className={`text-xl ${scrolled ? 'text-foreground-800' : 'text-background-50'} ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        <div
          className={`md:hidden overflow-hidden transition-all duration-300 bg-background-50 border-b border-background-200/70 ${
            mobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="px-4 py-4 space-y-1">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => { e.preventDefault(); handleNavClick(item.href); }}
                className="block px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
              >
                {item.label}
              </a>
            ))}
            <a
              href="#cards"
              onClick={(e) => { e.preventDefault(); handleNavClick('#cards'); }}
              className="block px-4 py-3 rounded-lg text-sm font-semibold bg-primary-500 text-background-50 text-center mt-2 cursor-pointer"
            >
              开始观影
            </a>
          </div>
        </div>
      </header>

      <main>
        <HeroSection />
        <IndexSection />
        <TopicsSection />
        <ContactSection />
        <AboutSection />
        <CardsSection />
        <RankingSection />
        <UpdatesSection />
        <FAQSection />
        <MediaSection />
        <FeaturesSection />
        <CommentsSection />
        <AppSection />
        <CarouselSection />
      </main>

      <footer id="footer" className="bg-background-900 text-foreground-300">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="py-12 md:py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
                  <i className="ri-movie-line text-base text-background-50"></i>
                </div>
                <span className="font-heading text-lg font-bold text-background-50">被窝影院</span>
              </div>
              <p className="text-xs text-foreground-400 leading-relaxed mb-4">
                被窝影院电影大全免费看，最新电影免费看和高分电影在线，电影大全无广告，院线电影免费经典电影全集，不缓冲流畅在线观看。
              </p>
              <div className="flex items-center gap-3">
                <a href="#hero" className="w-8 h-8 rounded-lg bg-background-800 flex items-center justify-center text-foreground-300 hover:text-accent-400 hover:bg-background-700 transition-colors cursor-pointer">
                  <i className="ri-wechat-line text-sm"></i>
                </a>
                <a href="#hero" className="w-8 h-8 rounded-lg bg-background-800 flex items-center justify-center text-foreground-300 hover:text-accent-400 hover:bg-background-700 transition-colors cursor-pointer">
                  <i className="ri-weibo-line text-sm"></i>
                </a>
                <a href="#hero" className="w-8 h-8 rounded-lg bg-background-800 flex items-center justify-center text-foreground-300 hover:text-accent-400 hover:bg-background-700 transition-colors cursor-pointer">
                  <i className="ri-bilibili-line text-sm"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-heading text-sm font-bold text-background-50 mb-4">快速导航</h4>
              <ul className="space-y-2.5">
                {[
                  { label: '精选专题', href: '#index' },
                  { label: '热门话题', href: '#topics' },
                  { label: '精选推荐', href: '#cards' },
                  { label: '热度榜单', href: '#ranking' },
                  { label: '最新动态', href: '#updates' },
                  { label: '影视解说', href: '#media' },
                ].map((link) => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                      className="text-xs text-foreground-400 hover:text-accent-400 transition-colors cursor-pointer"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-heading text-sm font-bold text-background-50 mb-4">电影分类</h4>
              <ul className="space-y-2.5">
                {['动作片', '喜剧片', '爱情片', '科幻片', '悬疑片', '恐怖片', '动画片', '纪录片'].map((cat) => (
                  <li key={cat}>
                    <a href="#cards" className="text-xs text-foreground-400 hover:text-accent-400 transition-colors cursor-pointer">{cat}</a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-heading text-sm font-bold text-background-50 mb-4">合作伙伴</h4>
              <p className="text-xs text-foreground-500 leading-relaxed">
                我们与众多优质内容平台保持良好合作，共同为影迷提供更丰富的观影选择。合作伙伴信息持续更新中。
              </p>
            </div>
          </div>

          <div className="border-t border-background-800 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-foreground-500">
              &copy; 2026 被窝影院 —— 电影大全免费看，不缓冲流畅在线观看
            </p>
            <div className="flex items-center gap-4">
              <a href="#faq" className="text-xs text-foreground-500 hover:text-foreground-300 transition-colors cursor-pointer">常见问题</a>
              <a href="#contact" className="text-xs text-foreground-500 hover:text-foreground-300 transition-colors cursor-pointer">联系我们</a>
              <a href="#about" className="text-xs text-foreground-500 hover:text-foreground-300 transition-colors cursor-pointer">关于我们</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}